/**
 * Centralized radar configuration constants
 *
 * This file contains all magic numbers and configuration values used in the radar visualization,
 * making them easy to adjust and maintain.
 */

/**
 * Radar dimension constants
 */
export const RADAR_DIMENSIONS = {
  /** Default radar width */
  defaultWidth: 1200,
  /** Default radar height */
  defaultHeight: 1000,
  /** Margin between radar edge and container */
  radiusMargin: 120,
  /** Radius of blip circles */
  blipRadius: 10,
  /** Stroke width for blips */
  blipStrokeWidth: 2,
} as const;

/**
 * Label positioning constants
 */
export const LABEL_POSITIONING = {
  /** Distance of quadrant labels from radar edge */
  quadrantLabelDistance: 35,
  /** Y-offset for ring labels */
  ringLabelYOffset: 20,
  /** Font size for ring labels */
  ringLabelFontSize: 14,
  /** Font weight for ring labels */
  ringLabelFontWeight: 600,
  /** Font size for quadrant labels */
  quadrantLabelFontSize: 13,
  /** Font weight for quadrant labels */
  quadrantLabelFontWeight: 600,
  /** Quadrant label angle spread (degrees from center) */
  quadrantAngleSpread: 40,
} as const;

/**
 * Legend configuration constants
 */
export const LEGEND_CONFIG = {
  /** X position of legend */
  x: 24,
  /** Y position of legend */
  y: 24,
  /** Height of each legend item */
  itemHeight: 28,
  /** Spacing between legend items */
  itemSpacing: 8,
  /** X position of legend items */
  itemX: 24,
  /** Padding around legend background */
  padding: 12,
  /** Border radius for legend background */
  borderRadius: 8,
  /** Font size for legend title */
  titleFontSize: 14,
  /** Font weight for legend title */
  titleFontWeight: 600,
  /** Font size for legend items */
  itemFontSize: 13,
} as const;

/**
 * Blip styling constants
 */
export const BLIP_STYLING = {
  /** Opacity for blips */
  opacity: 1,
  /** Opacity for blips on hover */
  hoverOpacity: 0.8,
  /** Stroke width for new blips */
  newStrokeWidth: 3,
  /** Cursor style for blips */
  cursor: 'pointer',
} as const;

/**
 * Ring configuration constants
 */
export const RING_CONFIG = {
  /** Opacity for ring backgrounds */
  fillOpacity: 0.05,
  /** Stroke width for ring borders */
  strokeWidth: 2,
  /** Opacity for ring borders */
  strokeOpacity: 0.3,
} as const;

/**
 * Quadrant configuration constants
 */
export const QUADRANT_CONFIG = {
  /** Angle for each quadrant (360 / 4) */
  anglePerQuadrant: 90,
  /** Number of quadrants */
  count: 4,
} as const;

/**
 * Animation and interaction constants
 */
export const INTERACTION_CONFIG = {
  /** Transition duration in milliseconds */
  transitionDuration: 200,
  /** Hover scale factor for blips */
  hoverScale: 1.2,
} as const;
